import { useState } from 'react';
import { Card, Group, Text, Stack, ActionIcon, Button, TextInput, Modal, Tooltip, Divider } from '@mantine/core';
import { IconPlus, IconStar, IconStarFilled, IconPencil, IconTrash, IconPhone, IconMapPin } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { PhoneInput } from './PhoneInput';
import { confirmDelete } from '../lib/confirm';
import { formatPhone } from '../lib/format';

interface Owner { studentId?: string; userId?: string }
interface PhoneRow { id: string; label: string; number: string; isPrimary: boolean }
interface AddressRow { id: string; label: string; address: string; isPrimary: boolean }

type EditState =
  | { kind: 'phone'; mode: 'add' | 'edit'; id?: string; label: string; value: string }
  | { kind: 'address'; mode: 'add' | 'edit'; id?: string; label: string; value: string }
  | null;

/**
 * O'quvchi yoki foydalanuvchi kontaktlari (telefon + manzil) jonli boshqaruvi.
 * Har biri label bilan, bittasi asosiy (primary). /phones va /addresses orqali.
 */
export function ContactsManager({
  owner, phones, addresses, queryKey, readOnly = false,
}: {
  owner: Owner;
  phones: PhoneRow[];
  addresses: AddressRow[];
  queryKey: unknown[];
  // readOnly — faqat ko'rish (mutatsiya endpointlariga ruxsati yo'q rollar uchun)
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [edit, setEdit] = useState<EditState>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey });

  const save = useMutation({
    mutationFn: async (e: NonNullable<EditState>) => {
      const path = e.kind === 'phone' ? '/phones' : '/addresses';
      const field = e.kind === 'phone' ? 'number' : 'address';
      const body: Record<string, unknown> = { label: e.label, [field]: e.value };
      if (e.mode === 'add') return api.post(path, { ...body, ...owner });
      return api.patch(`${path}/${e.id}`, body);
    },
    onSuccess: () => { notifications.show({ color: 'green', message: t('contactsMgr.saved') }); invalidate(); setEdit(null); },
  });

  const setPrimary = useMutation({
    mutationFn: (e: { kind: 'phone' | 'address'; id: string }) =>
      api.patch(`${e.kind === 'phone' ? '/phones' : '/addresses'}/${e.id}`, { isPrimary: true }),
    onSuccess: () => invalidate(),
  });

  const remove = useMutation({
    mutationFn: (e: { kind: 'phone' | 'address'; id: string }) =>
      api.delete(`${e.kind === 'phone' ? '/phones' : '/addresses'}/${e.id}`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('contactsMgr.deleted') }); invalidate(); },
  });

  const canSave = edit ? edit.label.trim().length > 0 && edit.value.trim().length > 0 : false;

  return (
    <Card>
      <Group justify="space-between" mb="xs">
        <Group gap={6}><IconPhone size={18} /><Text fw={600}>{t('contactsMgr.phones')}</Text></Group>
        {!readOnly && (
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
            onClick={() => setEdit({ kind: 'phone', mode: 'add', label: '', value: '' })}>{t('contactsMgr.add')}</Button>
        )}
      </Group>
      {phones.length === 0 ? (
        <Text size="sm" c="dimmed">{t('contactsMgr.noPhones')}</Text>
      ) : (
        <Stack gap={6}>
          {phones.map((p) => (
            <Group key={p.id} justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <Tooltip label={p.isPrimary ? t('contactsMgr.primaryPhone') : t('contactsMgr.makePrimary')}>
                  <ActionIcon variant="subtle" color={p.isPrimary ? 'yellow' : 'gray'}
                    onClick={() => !readOnly && !p.isPrimary && setPrimary.mutate({ kind: 'phone', id: p.id })} loading={setPrimary.isPending}>
                    {p.isPrimary ? <IconStarFilled size={16} /> : <IconStar size={16} />}
                  </ActionIcon>
                </Tooltip>
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>{formatPhone(p.number)}</Text>
                  <Text size="xs" c="dimmed">{p.label}{p.isPrimary ? ` · ${t('contactsMgr.primary')}` : ''}</Text>
                </div>
              </Group>
              {!readOnly && (
                <Group gap={2} wrap="nowrap">
                  <ActionIcon variant="subtle" onClick={() => setEdit({ kind: 'phone', mode: 'edit', id: p.id, label: p.label, value: p.number })}><IconPencil size={16} /></ActionIcon>
                  <ActionIcon variant="subtle" color="red" onClick={() => confirmDelete(p.number, () => remove.mutate({ kind: 'phone', id: p.id }))} loading={remove.isPending}><IconTrash size={16} /></ActionIcon>
                </Group>
              )}
            </Group>
          ))}
        </Stack>
      )}

      <Divider my="md" />

      <Group justify="space-between" mb="xs">
        <Group gap={6}><IconMapPin size={18} /><Text fw={600}>{t('contactsMgr.addresses')}</Text></Group>
        {!readOnly && (
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
            onClick={() => setEdit({ kind: 'address', mode: 'add', label: '', value: '' })}>{t('contactsMgr.add')}</Button>
        )}
      </Group>
      {addresses.length === 0 ? (
        <Text size="sm" c="dimmed">{t('contactsMgr.noAddresses')}</Text>
      ) : (
        <Stack gap={6}>
          {addresses.map((a) => (
            <Group key={a.id} justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <Tooltip label={a.isPrimary ? t('contactsMgr.primaryAddress') : t('contactsMgr.makePrimary')}>
                  <ActionIcon variant="subtle" color={a.isPrimary ? 'yellow' : 'gray'}
                    onClick={() => !readOnly && !a.isPrimary && setPrimary.mutate({ kind: 'address', id: a.id })} loading={setPrimary.isPending}>
                    {a.isPrimary ? <IconStarFilled size={16} /> : <IconStar size={16} />}
                  </ActionIcon>
                </Tooltip>
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>{a.address}</Text>
                  <Text size="xs" c="dimmed">{a.label}{a.isPrimary ? ` · ${t('contactsMgr.primary')}` : ''}</Text>
                </div>
              </Group>
              {!readOnly && (
                <Group gap={2} wrap="nowrap">
                  <ActionIcon variant="subtle" onClick={() => setEdit({ kind: 'address', mode: 'edit', id: a.id, label: a.label, value: a.address })}><IconPencil size={16} /></ActionIcon>
                  <ActionIcon variant="subtle" color="red" onClick={() => confirmDelete(a.address, () => remove.mutate({ kind: 'address', id: a.id }))} loading={remove.isPending}><IconTrash size={16} /></ActionIcon>
                </Group>
              )}
            </Group>
          ))}
        </Stack>
      )}

      <Modal
        opened={!!edit}
        onClose={() => setEdit(null)}
        title={edit?.kind === 'phone' ? t('contactsMgr.phone') : t('contactsMgr.address')}
        centered
      >
        {edit && (
          <Stack>
            <TextInput label={t('contactsMgr.labelName')} required value={edit.label}
              onChange={(e) => setEdit({ ...edit, label: e.currentTarget.value })} />
            {edit.kind === 'phone' ? (
              <PhoneInput label={t('contactsMgr.number')} value={edit.value} onChange={(v: string) => setEdit({ ...edit, value: v })} />
            ) : (
              <TextInput label={t('contactsMgr.address')} required value={edit.value}
                onChange={(e) => setEdit({ ...edit, value: e.currentTarget.value })} />
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEdit(null)}>{t('contactsMgr.cancel')}</Button>
              <Button disabled={!canSave} loading={save.isPending} onClick={() => save.mutate(edit)}>{t('contactsMgr.save')}</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Card>
  );
}
